import React, { useEffect, useState, useMemo } from "react";
import AppShell from "../components/AppShell";
import { api, API } from "../lib/api";
import { fmtDate, fmtMoneyFull } from "../lib/format";
import { toast } from "sonner";
import { FilePdf, Bank, MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";

const KIND_LABEL = {
  purchase_agreement: "Purchase Agreement",
  assignment: "Assignment of Contract",
};

const KIND_ACCENT = {
  purchase_agreement: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  assignment: "border-amber-500/40 text-amber-300 bg-amber-500/10",
};

export default function DocsVault() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const r = await api.get("/documents"); setRows(r.data); }
    catch { toast.error("Failed to load documents"); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(d => (d.property || "").toLowerCase().includes(term) || (d.filename || "").toLowerCase().includes(term));
  }, [rows, q]);

  const dlUrl = (docId) => {
    const t = localStorage.getItem("jr_token");
    return `${API}/documents/${docId}/download?token=${encodeURIComponent(t)}`;
  };

  const sendToTitle = async (leadId) => {
    if (!leadId) { toast.error("Document is not linked to a lead"); return; }
    setBusy(leadId);
    try {
      const r = await api.post(`/leads/${leadId}/title-handoff`);
      toast.success(`Title package sent (${r.data.attachments} attachments)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send to Title failed");
    } finally { setBusy(null); }
  };

  const groups = useMemo(() => {
    const byLead = new Map();
    for (const d of filtered) {
      if (!byLead.has(d.lead_id)) byLead.set(d.lead_id, { lead_id: d.lead_id, property: d.property, stage: d.stage, pa_signed_at: d.pa_signed_at, assignment_signed_at: d.assignment_signed_at, title_handoff_at: d.title_handoff_at, docs: [] });
      byLead.get(d.lead_id).docs.push(d);
    }
    return Array.from(byLead.values());
  }, [filtered]);

  const readyForTitle = (g) => !!(g.pa_signed_at && g.assignment_signed_at);
  const alreadySent = (g) => !!g.title_handoff_at;

  return (
    <AppShell header={
      <div className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between gap-3">
        <div>
          <div className="overline">Closing Control Center</div>
          <h1 className="text-lg font-semibold flex items-center gap-2"><FilePdf size={18} weight="bold" /> Docs Vault</h1>
        </div>
        <div className="relative w-full max-w-xs hidden md:block">
          <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search property or filename"
            data-testid="docs-search"
            className="w-full bg-[#18181b] hairline pl-7 pr-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
        </div>
      </div>
    }>
      <div className="p-4 lg:p-6">
        <div className="md:hidden mb-3 relative">
          <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search"
            className="w-full bg-[#18181b] hairline pl-7 pr-2 py-1.5 mono text-xs rounded-sm focus:outline-none focus:border-emerald-500" />
        </div>

        {groups.length === 0 && (
          <div className="border border-zinc-800/80 bg-[#0b0b0e] rounded-sm p-8 text-center" data-testid="docs-empty">
            <div className="overline mb-2">Vault Empty</div>
            <div className="text-sm text-zinc-400">Purchase Agreements and Assignment contracts land here the moment they're generated.</div>
          </div>
        )}

        <div className="space-y-3" data-testid="docs-vault-list">
          {groups.map(g => (
            <div key={g.lead_id} className="border border-zinc-800/80 bg-[#0b0b0e] rounded-sm" data-testid={`vault-deal-${g.lead_id}`}>
              <div className="px-4 py-3 border-b border-zinc-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{g.property || g.lead_id}</div>
                  <div className="text-[11px] mono text-zinc-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                    <span>{g.docs.length} document{g.docs.length > 1 ? "s" : ""}</span>
                    {g.pa_signed_at && <span className="text-emerald-400">PA signed {fmtDate(g.pa_signed_at)}</span>}
                    {g.assignment_signed_at && <span className="text-emerald-400">Assign signed {fmtDate(g.assignment_signed_at)}</span>}
                    {g.title_handoff_at && <span className="text-blue-300">Sent to title {fmtDate(g.title_handoff_at)}</span>}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {alreadySent(g) ? (
                    <span className="text-[10px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border border-blue-500/50 text-blue-300 bg-blue-500/10 inline-flex items-center gap-1.5" data-testid={`title-sent-${g.lead_id}`}>
                      <CheckCircle size={11} weight="fill" /> Title package sent
                    </span>
                  ) : (
                    <button
                      onClick={() => sendToTitle(g.lead_id)}
                      disabled={!readyForTitle(g) || busy === g.lead_id}
                      data-testid={`send-to-title-${g.lead_id}`}
                      title={readyForTitle(g) ? "Send full contract package + wire details to title company" : "Both PA and Assignment must be signed first"}
                      className={`text-[11px] mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm inline-flex items-center gap-2 transition-colors ${readyForTitle(g) ? "bg-emerald-500 hover:bg-emerald-400 text-black font-semibold" : "border border-zinc-700 text-zinc-500 cursor-not-allowed"}`}>
                      <Bank size={13} weight="bold" /> {busy === g.lead_id ? "Sending…" : "Send to Title"}
                    </button>
                  )}
                </div>
              </div>
              <ul className="divide-y divide-zinc-800/70">
                {g.docs.map(d => (
                  <li key={d.id} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex items-center gap-2.5">
                      <span className={`text-[10px] mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm border ${KIND_ACCENT[d.kind] || "border-zinc-700 text-zinc-400"}`}>
                        {KIND_LABEL[d.kind] || d.kind}
                      </span>
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{d.filename}</div>
                        <div className="text-[10px] mono text-zinc-500">{fmtDate(d.created_at)}</div>
                      </div>
                    </div>
                    <a href={dlUrl(d.id)} target="_blank" rel="noreferrer" data-testid={`doc-download-${d.id}`}
                       className="text-[10px] mono uppercase tracking-[0.15em] px-2.5 py-1.5 rounded-sm border border-zinc-700 hover:border-zinc-500 shrink-0">
                      PDF ↓
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </AppShell>
  );
}
